import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  SearchableDropdownComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import { useAppDispatch } from "../../state/hooks";
import { fetchLookup } from "../../state/slices/lookupsSlice";
import { QuickAddModal } from "./QuickAddModal";

interface SearchableDropdownProps {
  config: SearchableDropdownComponent;
  value?: string | number | boolean | (string | number | boolean)[];
  onChange?: (
    name: string,
    value: string | number | boolean | (string | number | boolean)[]
  ) => void;
  onSearch?: (searchTerm: string) => void;
  onFocus?: () => void;
  externalError?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  config,
  value,
  onChange,
  onSearch,
  onFocus,
  externalError,
}) => {
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const options = Array.isArray(config.options) ? config.options : [];

  /* ------------------------------ VALIDATION ------------------------------ */

  const validateField = (inputValue: any): string => {
    if (!config.validation) return "";
    for (const rule of config.validation) {
      if (
        rule.rule === ValidationRule.REQUIRED &&
        (inputValue === undefined ||
          inputValue === "" ||
          (Array.isArray(inputValue) && inputValue.length === 0))
      ) {
        return rule.message || "Please select an option";
      }
    }
    return "";
  };

  /* ------------------------------ VALUE MATCHING ------------------------------ */

  const matchValue = (
    optionValue: string | number | boolean,
    searchValue?: string | number | boolean
  ): boolean => {
    if (searchValue === undefined || searchValue === null) return false;

    if (optionValue === searchValue) return true;
    if (String(optionValue) === String(searchValue)) return true;

    const optNum = Number(optionValue);
    const searchNum = Number(searchValue);
    if (!isNaN(optNum) && !isNaN(searchNum) && optNum === searchNum) return true;

    if (typeof optionValue === "boolean") {
      if (searchValue === "true") return optionValue === true;
      if (searchValue === "false") return optionValue === false;
    }

    if (typeof searchValue === "boolean") {
      if (optionValue === "true") return searchValue === true;
      if (optionValue === "false") return searchValue === false;
    }

    return false;
  };

  /* ------------------------------ FILTER ------------------------------ */

  const filteredOptions = options.filter((opt) =>
    (opt?.label ?? opt?.value ?? "").toString().toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  // Reset option refs array when filtered options change
  useEffect(() => {
    optionRefs.current = new Array(filteredOptions.length).fill(null);
  }, [filteredOptions.length]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length && filteredOptions.length > 0) {
      setHighlightedIndex(0);
    } else if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(-1);
    }
  }, [filteredOptions.length, highlightedIndex]);

  // Initialize highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen && filteredOptions.length > 0 && highlightedIndex === -1) {
      // If there's a current value, highlight it; otherwise highlight first option
      if (!config.multiple && value !== undefined && value !== "") {
        const currentIndex = filteredOptions.findIndex((opt) =>
          matchValue(opt.value, value as any)
        );
        if (currentIndex >= 0) {
          setHighlightedIndex(currentIndex);
          setTimeout(() => {
            const optionElement = optionRefs.current[currentIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 50);
        } else {
          setHighlightedIndex(0);
        }
      } else {
        setHighlightedIndex(0);
      }
    } else if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen, filteredOptions.length, value, config.multiple]);

  /* ------------------------------ AUTO FIX (LABEL → VALUE) ------------------------------ */

  const autoFixedRef = useRef(false);

  useEffect(() => {
    if (
      autoFixedRef.current ||
      config.multiple ||
      !value ||
      options.length === 0
    )
      return;

    if (options.some((opt) => matchValue(opt.value, value as any))) return;

    const labelMatch = options.find(
      (opt) => (opt?.label ?? "").toString().toLowerCase() === String(value ?? "").toLowerCase()
    );

    if (labelMatch) {
      autoFixedRef.current = true;
      onChange?.(config.name, labelMatch.value);
    }
  }, [value, options]);

  /* ------------------------------ DISPLAY ------------------------------ */

  const getDisplayValue = () => {
    if (options.length === 0) return "";

    if (config.multiple && Array.isArray(value)) {
      return value
        .map((v) => options.find((opt) => matchValue(opt.value, v))?.label)
        .filter(Boolean)
        .join(", ");
    }

    const selectedOption = options.find((opt) =>
      matchValue(opt.value, value as any)
    );

    return selectedOption?.label || "";
  };

  const hasValue = config.multiple
    ? Array.isArray(value) && value.length > 0
    : value !== undefined && value !== "";

  const handleQuickAddSuccess = (response: any) => {
    if (config.lookupName) {
      dispatch(fetchLookup({ lookupName: config.lookupName }));
    }

    // Auto-select the new item
    const newId = response?.data?.id || response?.id || response?.data?.uuid || response?.uuid;
    if (newId) {
      onChange?.(config.name, newId);
    }
  };


  /* ------------------------------ HANDLERS ------------------------------ */

  const handleOptionClick = (optionValue: any) => {
    if (config.multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      let newValue: (string | number | boolean)[];

      if (currentValue.includes(optionValue)) {
        newValue = currentValue.filter((v) => v !== optionValue);
      } else {
        if (
          config.maxSelections &&
          currentValue.length >= config.maxSelections
        ) {
          return;
        }
        newValue = [...currentValue, optionValue];
      }

      const err = validateField(newValue);
      setError(err);
      onChange?.(config.name, newValue);
    } else {
      const err = validateField(optionValue);
      setError(err);
      onChange?.(config.name, optionValue);
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Tab key - allow normal tab navigation, just close dropdown
    if (e.key === "Tab") {
      if (isOpen) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
      // Don't prevent default - let Tab work naturally
      return;
    }

    // Handle Escape key
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
      inputRef.current?.blur();
      return;
    }

    // Handle Enter key
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        // Select highlighted option
        handleOptionClick(filteredOptions[highlightedIndex].value);
      } else if (!isOpen) {
        // Open dropdown if closed
        setIsOpen(true);
        if (filteredOptions.length > 0) {
          setHighlightedIndex(0);
        }
      }
      return;
    }

    // Handle ArrowDown key
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      
      // Ensure input stays focused
      if (inputRef.current) {
        inputRef.current.focus();
      }
      
      if (!isOpen) {
        // Open dropdown and highlight first option
        setIsOpen(true);
        setTimeout(() => {
          if (filteredOptions.length > 0) {
            setHighlightedIndex(0);
          }
        }, 10);
      } else {
        // Navigate to next option
        setHighlightedIndex((prev) => {
          const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : (prev === -1 ? 0 : prev);
          // Scroll into view
          setTimeout(() => {
            const optionElement = optionRefs.current[nextIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 0);
          return nextIndex;
        });
      }
      return;
    }

    // Handle ArrowUp key
    if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      
      // Ensure input stays focused
      if (inputRef.current) {
        inputRef.current.focus();
      }
      
      if (!isOpen) {
        // Open dropdown and highlight last option
        setIsOpen(true);
        setTimeout(() => {
          if (filteredOptions.length > 0) {
            setHighlightedIndex(filteredOptions.length - 1);
          }
        }, 10);
      } else {
        // Navigate to previous option
        setHighlightedIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : (prev === -1 ? filteredOptions.length - 1 : 0);
          // Scroll into view
          setTimeout(() => {
            const optionElement = optionRefs.current[nextIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 0);
          return nextIndex;
        });
      }
      return;
    }
  };

  const handleClear = () => {
    onChange?.(config.name, config.multiple ? [] : "");
    setError("");
    setSearchTerm("");
  };

  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  /* ------------------------------ EFFECTS ------------------------------ */

  // Add capture phase listener to intercept arrow keys before global handler
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleCapture = (e: KeyboardEvent) => {
      // Only intercept arrow keys when they're on our searchable dropdown input
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const target = e.target as HTMLElement;
        // Check if target is our input or has the searchable dropdown attribute
        if (target === input || (target instanceof HTMLInputElement && target.getAttribute('data-searchable-dropdown') === 'true')) {
          // Mark the event so global handler knows to skip it
          (e as any).__searchableDropdownHandled = true;
          // Don't stop propagation - let React's handler run, but global handler will skip it
        }
      }
    };

    // Use capture phase on window to run before global handler (which is in bubble phase)
    window.addEventListener("keydown", handleCapture, true);
    
    return () => {
      window.removeEventListener("keydown", handleCapture, true);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target) return;

      // Check if click is outside all dropdown elements
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);
      const isOutsideMenu = !dropdownMenuRef.current || !dropdownMenuRef.current.contains(target);
      const isOutsideInput = !inputRef.current || !inputRef.current.contains(target);

      // Only close if click is outside all elements
      if (isOutsideDropdown && isOutsideMenu && isOutsideInput) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    // Use capture phase to catch clicks before they bubble
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isOpen]);

  /* ------------------------------ RENDER ------------------------------ */

  return (
    <div className="relative w-full px-2" ref={dropdownRef} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div className="flex gap-2 items-center">
        <div className="relative flex-1 flex items-center">
          <input
            ref={inputRef}
            type="text"
            placeholder={hasValue ? "" : config.placeholder || "Search..."}
            value={isOpen ? searchTerm : ""}
            data-searchable-dropdown="true"
            data-dropdown-open={isOpen ? "true" : "false"}
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-controls={isOpen ? `dropdown-${config.name}` : undefined}
            aria-activedescendant={isOpen && highlightedIndex >= 0 ? `option-${config.name}-${highlightedIndex}` : undefined}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              onSearch?.(e.target.value);
              // Reset highlighted index when search term changes
              setHighlightedIndex(-1);
            }}
            onFocus={() => {
              setIsOpen(true);
              setSearchTerm("");
              setHighlightedIndex(-1);
              onFocus?.();
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // No longer needed due to onMouseDown preventDefault fix
            }}
            disabled={config.disabled}
            className={`w-full p-2 px-4 text-sm font-normal text-left outline-none rounded-md font-sans dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${config.disabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-text"} ${error
              ? "border border-danger-500 dark:border-danger-500"
              : isOpen
                ? "border border-primary-500 dark:border-primary-400"
                : "border border-gray-300 dark:border-gray-600"
              } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
          />

          {hasValue && !isOpen && options.length > 0 && (
            <div className="absolute left-4 right-12 top-1/2 -translate-y-1/2 pointer-events-none text-sm font-normal text-gray-900 dark:text-gray-100 overflow-hidden text-ellipsis whitespace-nowrap">
              {getDisplayValue()}
            </div>
          )}

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {config.clearable && hasValue && (
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input from losing focus
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const willOpen = !isOpen;
                setIsOpen(willOpen);
                
                if (willOpen) {
                  // When opening, initialize highlighted index
                  setTimeout(() => {
                    inputRef.current?.focus();
                    if (filteredOptions.length > 0) {
                      if (!config.multiple && value !== undefined && value !== "") {
                        const currentIndex = filteredOptions.findIndex((opt) =>
                          matchValue(opt.value, value as any)
                        );
                        setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
                      } else {
                        setHighlightedIndex(0);
                      }
                    }
                  }, 0);
                } else {
                  setHighlightedIndex(-1);
                }
              }}
            >
              <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {config.PopupScreenId && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsQuickAddOpen(true);
            }}
            className="p-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors h-[38px] w-[38px] flex items-center justify-center shadow-sm"
            title="Add New"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            id={`dropdown-${config.name}`}
            role="listbox"
            className="dark:bg-gray-800 !w-fit border border-gray-300 dark:border-gray-600 max-h-[400px] overflow-y-auto z-[9999] shadow-md bg-gray-50"
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="p-2 px-4 text-sm font-normal text-gray-500 dark:text-gray-400 italic">No options found</div>
            ) : (
              filteredOptions.map((option, idx) => (
                <div
                  key={idx}
                  id={`option-${config.name}-${idx}`}
                  ref={(el) => {
                    optionRefs.current[idx] = el;
                  }}
                  role="option"
                  aria-selected={highlightedIndex === idx || matchValue(option.value, value as any)}
                  tabIndex={-1}
                  className={`p-2 px-4 text-sm font-normal text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 font-sans border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors duration-200 ${
                    highlightedIndex === idx
                      ? "bg-primary-200 dark:bg-primary-800/70 text-primary-900 dark:text-primary-100 font-medium"
                      : matchValue(option.value, value as any)
                        ? "bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 font-medium"
                        : ""
                  }`}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input from losing focus
                    handleOptionClick(option.value);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.label}</span>
                    {config.multiple &&
                      Array.isArray(value) &&
                      value.includes(option.value) && (
                        <span className="text-success-600 dark:text-success-400 font-bold">
                          ✓
                        </span>
                      )}
                  </div>
                </div>
              ))
            )}
          </div>,
          document.body
        )}

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-sm mt-1">⚠️ {error || externalError}</div>
      )}
      {config.PopupScreenId && (
        <QuickAddModal
          isOpen={isQuickAddOpen}
          popupScreenId={config.PopupScreenId}
          onClose={() => setIsQuickAddOpen(false)}
          onSuccess={handleQuickAddSuccess}
        />
      )}
    </div>
  );
};
